import { BreakpointKey } from '../../../../theming';
import { ResponsiveCropDimensions } from '../../ImageGallery.types';

/**
 * Generates responsive crop dimensions from a set of widths and an aspect ratio.
 *
 * - Rounds each provided width **up** to the nearest 10.
 * - Calculates the matching height using the given aspect ratio.
 * - Rounds each height **up** to the nearest 10.
 * - Returns a `ResponsiveCropDimensions` object keyed by the standard breakpoints (`xs`, `s`, `m`, `l`).
 *
 * @param widths - An array of four widths (one per breakpoint) in order: `xs`, `s`, `m`, `l`.
 *                 The array is `readonly` to prevent accidental modification.
 * @param ratio - The aspect ratio as `width / height` (e.g. `1.5` for 3:2, `0.75` for 3:4, `1` for square).
 * @returns An object mapping each breakpoint to its calculated width and height.
 */
export function generateCropDimensions(widths: readonly number[], ratio: number): ResponsiveCropDimensions {
    const breakpoints: BreakpointKey[] = ['xs', 's', 'm', 'l'];

    return breakpoints.reduce((acc, bp, i) => {
        const roundedW = Math.ceil(widths[i] / 10) * 10;
        const roundedH = Math.ceil(roundedW / ratio / 10) * 10;
        acc[bp] = { w: roundedW, h: roundedH };
        return acc;
    }, {} as ResponsiveCropDimensions);
}
